import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Check, Loader2, Home } from 'lucide-react';
import { expensesAPI } from '@/services/api';
import { useAuth } from '@/context/AuthContext';

const ExpenseConfirmPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  
  const [expense, setExpense] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState('');
  const [transactionId, setTransactionId] = useState('');

  useEffect(() => {
    fetchExpense();
  }, [id]);

  const fetchExpense = async () => {
    try {
      setLoading(true);
      const response = await expensesAPI.get(id);
      setExpense(response.data);
      
      // Check if already settled
      if (response.data.status === 'settled') {
        setConfirmed(true);
      }
    } catch (err) {
      setError('Expense not found');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    setConfirming(true);
    setError('');

    try {
      await expensesAPI.settle(id, transactionId || null);
      setConfirmed(true);
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to confirm payment';
      setError(typeof message === 'string' ? message : 'Failed to confirm payment');
    } finally {
      setConfirming(false);
    }
  };

  const formatCurrency = (amount) => {
    const num = Math.abs(amount);
    if (num === Math.floor(num)) {
      return `₹${num.toLocaleString('en-IN')}`;
    }
    return `₹${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (error && !expense) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6">
        <p className="text-lg font-medium mb-4">{error}</p>
        <button onClick={() => navigate('/home')} className="btn-secondary">
          Go Home
        </button>
      </div>
    );
  }

  const isPayer = expense?.paid_by_user_id === user?.id;
  const split = expense?.splits?.[0];
  const amountOwed = split?.amount_owed || 0;

  // Determine friend info
  const friendName = isPayer 
    ? (split?.user_name || split?.owed_by_phone || 'Friend')
    : (expense?.paid_by_name || expense?.paid_by_phone || 'Friend');

  // Success state
  if (confirmed) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6">
        <div className="card-base text-center max-w-sm w-full">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <Check className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-heading font-bold mb-2">
            Payment Confirmed!
          </h1>
          <p className="text-neutral-600 mb-6">
            Expense marked as settled
          </p>
          <div className="space-y-3">
            <button
              onClick={() => navigate(`/expenses/${id}`)}
              className="btn-secondary w-full"
              data-testid="view-expense-button"
            >
              View Expense
            </button>
            <button
              onClick={() => navigate('/home')}
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="go-home-button"
            >
              <Home className="w-5 h-5" />
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="sticky top-0 bg-white z-10 px-6 py-4 flex items-center gap-4 border-b-2 border-black">
        <button 
          onClick={() => navigate(`/expenses/${id}`)}
          className="hover:opacity-70 transition-opacity"
          data-testid="back-button"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h1 className="text-xl font-heading font-bold">Confirm Payment</h1>
      </header>

      <main className="flex-1 p-6 pb-32 space-y-6">
        {/* Confirmation Card */}
        <div className="card-base text-center">
          <p className="text-neutral-500 mb-4">
            {isPayer ? 'Confirm you received' : 'Confirm you paid'}
          </p>
          <p className="text-5xl font-heading font-bold mb-4">
            {formatCurrency(amountOwed)}
          </p>
          <p className="text-lg">
            {isPayer ? 'from' : 'to'}{' '}
            <span className="font-bold">{friendName}</span>
          </p>
          
          {/* Avatar */}
          <div className="w-16 h-16 rounded-full bg-brand-purple text-white flex items-center justify-center font-bold text-2xl mx-auto mt-6">
            {friendName[0]?.toUpperCase() || 'F'}
          </div>
        </div>

        {/* Transaction ID (optional) */}
        <div>
          <label className="text-sm font-medium text-neutral-500 uppercase tracking-wide block mb-2">
            UPI Transaction ID (optional)
          </label>
          <input
            type="text"
            value={transactionId}
            onChange={(e) => setTransactionId(e.target.value)}
            placeholder="Enter if available"
            className="input-base"
            data-testid="transaction-id-input"
          />
          <p className="text-xs text-neutral-400 mt-1">
            Helps with record keeping
          </p>
        </div>

        {error && (
          <p className="text-error text-sm font-medium text-center" data-testid="error-message">
            {error}
          </p>
        )}
      </main>

      {/* Fixed Bottom */}
      <div className="fixed bottom-0 left-0 right-0 p-6 bg-white border-t-2 border-black space-y-3">
        <button
          onClick={handleConfirm}
          disabled={confirming}
          className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="confirm-payment-button"
        >
          {confirming ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Confirming...
            </>
          ) : (
            <>
              <Check className="w-5 h-5" />
              Confirm Payment
            </>
          )}
        </button>
        <p className="text-xs text-neutral-500 text-center">
          This will mark the expense as settled for both of you
        </p>
      </div>
    </div>
  );
};

export default ExpenseConfirmPage;
